// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Mario World Universe - Star Systems Configuration
 * 
 * The Mushroom Kingdom and its neighboring worlds across the galaxy.
 * Each system represents a different kingdom or location from the Mario universe.
 */

export const starSystems = [
  // STARTING SYSTEM - Mushroom Kingdom
  {
    id: 1000,
    name: 'Mushroom Kingdom',
    type: 'planet',
    description: 'The peaceful home of Princess Peach and the Toads. Rolling green hills dotted with question blocks and warp pipes.',
    scanDescription: 'Detecting high concentrations of Super Mushrooms and Fire Flowers. Multiple castle structures visible.',
    distance: 0,
    coordinates: { x: 45, y: 50, z: 0 },
    threat: 'LOW',
    resources: 'HIGH',
    services: ['refuel'],
    tradeType: 'agricultural',
    primaryColor: '#FF0000',
    secondaryColor: '#00FF00',
    hasRings: false
  },

  {
    id: 1001,
    name: "Bowser's Galaxy",
    type: 'planet',
    description: 'Dark volcanic world ruled by the Koopa King. Lava flows and fortress towers dominate the landscape.',
    scanDescription: 'Warning: Hostile Koopa forces detected. Extreme heat signatures from lava flows.',
    distance: 18,
    coordinates: { x: 60, y: 55, z: 3 },
    threat: 'HIGH',
    resources: 'MEDIUM',
    services: ['refuel'],
    tradeType: 'military',
    primaryColor: '#FF0000',
    secondaryColor: '#FFFF00',
    hasRings: false
  },

  {
    id: 1002,
    name: 'Star Road',
    type: 'station',
    description: 'Mystical space station built around a cosmic pathway. Wishes and star power flow through its corridors.',
    scanDescription: 'Unusual energy readings detected. Station appears to be constructed from crystallized star power.',
    distance: 22,
    coordinates: { x: 30, y: 60, z: -5 },
    threat: 'LOW',
    resources: 'FUEL DEPOT',
    services: ['refuel'],
    tradeType: 'station_advanced',
    primaryColor: '#FFFF00',
    secondaryColor: '#FF0000',
    hasRings: true
  },

  {
    id: 1003,
    name: "Yoshi's Island",
    type: 'planet',
    description: 'Tropical paradise inhabited by friendly dinosaurs. Colorful flowers and fruit trees cover the islands.',
    scanDescription: 'Life signs abundant. Multiple Yoshi species detected across various island chains.',
    distance: 16,
    coordinates: { x: 55, y: 35, z: 8 },
    threat: 'LOW',
    resources: 'HIGH',
    services: ['refuel'],
    tradeType: 'agricultural',
    primaryColor: '#00FF00',
    secondaryColor: '#FFFF00',
    hasRings: false
  },

  {
    id: 1004,
    name: 'Sarasaland',
    type: 'planet',
    description: 'Four diverse kingdoms united under Princess Daisy. From ancient pyramids to underwater cities.',
    scanDescription: 'Four distinct biomes detected: desert, marine, easter island, and chai kingdom regions.',
    distance: 25,
    coordinates: { x: 40, y: 70, z: 2 },
    threat: 'MODERATE',
    resources: 'HIGH',
    services: ['refuel'],
    tradeType: 'luxury',
    primaryColor: '#FFFF00',
    secondaryColor: '#FF0000',
    hasRings: false
  },

  {
    id: 1005,
    name: 'Isle Delfino',
    type: 'planet',
    description: 'Tropical resort world with crystal-clear waters. Piantas and Nokis welcome visitors to their paradise.',
    scanDescription: 'Resort facilities detected. Warning: Occasional graffiti-based pollution incidents reported.',
    distance: 20,
    coordinates: { x: 65, y: 45, z: -3 },
    threat: 'LOW',
    resources: 'MEDIUM',
    services: ['refuel'],
    tradeType: 'luxury',
    primaryColor: '#00FF00',
    secondaryColor: '#FFFF00',
    hasRings: false
  },

  {
    id: 1006,
    name: 'Comet Observatory',
    type: 'station',
    description: 'Rosalina\'s mobile observatory travels between galaxies. Home to the Lumas and gateway to distant stars.',
    scanDescription: 'Massive mobile structure detected. Powered by Star Bits and Luma energy.',
    distance: 28,
    coordinates: { x: 25, y: 45, z: 10 },
    threat: 'LOW',
    resources: 'FUEL DEPOT',
    services: ['refuel'],
    tradeType: 'station_advanced',
    primaryColor: '#FFFF00',
    secondaryColor: '#00FF00',
    hasRings: true
  },

  {
    id: 1007,
    name: 'Diamond City',
    type: 'planet',
    description: 'Wario\'s industrial stronghold. Factories and treasure vaults sprawl across the urban landscape.',
    scanDescription: 'Heavy industrial activity detected. Massive treasure hoards and garlic production facilities.',
    distance: 24,
    coordinates: { x: 50, y: 65, z: -8 },
    threat: 'MODERATE',
    resources: 'VERY HIGH',
    services: ['refuel'],
    tradeType: 'industrial',
    primaryColor: '#FFFF00',
    secondaryColor: '#FF0000',
    hasRings: false
  },

  {
    id: 1008,
    name: 'Subcon',
    type: 'planet',
    description: 'Dream world where reality bends to imagination. Vegetables grow as weapons and shy guys roam freely.',
    scanDescription: 'Unusual physics detected. Dream logic appears to govern natural laws.',
    distance: 26,
    coordinates: { x: 35, y: 35, z: 5 },
    threat: 'MODERATE',
    resources: 'MEDIUM',
    services: ['refuel'],
    tradeType: 'remote',
    primaryColor: '#FF0000',
    secondaryColor: '#00FF00',
    hasRings: false
  },

  {
    id: 1009,
    name: 'Dark Land',
    type: 'planet',
    description: 'Bowser\'s original stronghold in the outer reaches. Dark skies and twisted pipes create an ominous atmosphere.',
    scanDescription: 'Hostile environment detected. Koopa military installations and dark magic signatures.',
    distance: 30,
    coordinates: { x: 60, y: 30, z: -10 },
    threat: 'VERY HIGH',
    resources: 'LOW',
    services: ['refuel'],
    tradeType: 'military',
    primaryColor: '#FF0000',
    secondaryColor: '#FFFF00',
    hasRings: false
  },

  // BLACK HOLE
  {
    id: 999,
    name: 'Black Hole',
    type: 'blackhole',
    description: 'A massive gravitational anomaly that warps space-time throughout the Mario universe. Even Bowser fears its power.',
    scanDescription: 'Extreme gravitational distortion detected. Approach not recommended.',
    distance: 26000,
    coordinates: { x: 42, y: 48, z: -12 },
    threat: 'EXTREME',
    resources: 'NONE',
    services: [],
    tradeType: 'none',
    primaryColor: '#000000',
    secondaryColor: '#FF0000',
    hasRings: true
  }
];

// Star positions for map visualization
export const starPositions = starSystems.reduce((acc, system) => {
  acc[system.id] = system.coordinates;
  return acc;
}, {});